import type React from 'react';
import { useRef, useState } from 'react';
import { ButtonGroup, Icon, IconButton, Menu, Tag } from '@zillow/constellation-10';
import { IconMoreFilled } from '@zillow/constellation-icons';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import { formatDate, printDaysBetweenDates } from '@common/paymentsUtils/dateUtils';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { capitalizeFirstLetter } from '@common/utils/stringUtils';
import TransactionConstants from '@common/constants/TransactionConstants';
import ONE_TIME_PAYMENT_TYPE from '@common/constants/oneTimePaymentTypes';
import DepositDateTooltipComponent from '@rental-manager/components/StyledTooltip/DepositDateToolTip';
import type { ITransactionModel } from '@rental-manager/models/Types';
import { getFirstName } from '@rental-manager/models/PropertyInfo';
import MarkAsPaidAction from './MarkAsPaidAction';
import OfflineMarkNote from './OfflineMarkNote';
import UndoOfflineMarkAction from './UndoOfflineMarkAction';

const getStatusText = (status: string, dateReceived: Date | string): React.ReactNode => {
  switch (status) {
    case TransactionConstants.STATUS.PENDING:
      return (
        <Text type="body" className="fs-exclude">
          Deposit date: {formatDate(dateReceived, 'M/d/yy')}
          <DepositDateTooltipComponent />
        </Text>
      );
    case TransactionConstants.STATUS.COMPLETE:
      return <Text className="fs-exclude">Deposited: {formatDate(dateReceived, 'M/d/yy')}</Text>;
    case TransactionConstants.STATUS.REFUNDED:
    case TransactionConstants.STATUS.DISPUTED:
    case TransactionConstants.STATUS.PAYMENT_FAILED:
    case TransactionConstants.STATUS.DEPOSIT_FAILED:
    case TransactionConstants.STATUS.UNPAID:
    case TransactionConstants.STATUS.PAST_DUE:
    default:
      return <></>;
  }
};

const getPaymentSent = (status: string, dateCreated: Date | string): React.ReactNode => {
  if (status === TransactionConstants.STATUS.PENDING || status === TransactionConstants.STATUS.COMPLETE) {
    return <Text className="fs-exclude">Payment sent: {formatDate(dateCreated, 'M/d/yy')}</Text>;
  }
  return <></>;
};

const getPaymentTypeLabel = (paymentType: string, paymentPlanInstallmentTitle = ''): string => {
  switch (paymentType) {
    case ONE_TIME_PAYMENT_TYPE.OTHER:
      return `${capitalizeFirstLetter(paymentType)} payment`;
    case ONE_TIME_PAYMENT_TYPE.LATE_FEE:
      return 'Late fee';
    case ONE_TIME_PAYMENT_TYPE.PET_FEE:
      return 'Pet fee';
    case ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN:
      return paymentPlanInstallmentTitle;
    default:
      return capitalizeFirstLetter(paymentType);
  }
};

const getBankType = (category: string, accountType: string): string => {
  if (category === 'upcoming') return '';
  switch (accountType) {
    case 'credit':
      return 'Credit card';
    case 'debit':
      return 'Debit card';
    case 'bank_account':
      return 'ACH transfer';
    default:
      return '';
  }
};

type Props = {
  transaction: ITransactionModel;
  cancelOneTimePayment: ({
    otpKey,
    tenantFirstName,
    paymentType,
    paymentPlanInstallmentTitle,
  }: Record<string, string>) => void;
  today: Date | string;
  remindTenantToPayOneTimePayment: ({ tenantEmail, requestKey }) => void;
};

const OneTimePaymentsRow: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const { transaction, cancelOneTimePayment, today = new Date() } = props;
  const {
    groupKey,
    requestKey,
    propertyId,
    paymentCategory,
    tenant,
    accountType,
    landlordStatus,
    paymentType,
    notes,
    manualPaymentNotes,
    dateRequested,
    dateCreated,
    dateReceived,
    amount,
    showPaidOffline,
    paymentPlanInstallmentTitle = '',
  } = transaction;
  const tenantFirstName = getFirstName(tenant.name);
  const paymentTypeLabel = getPaymentTypeLabel(paymentType, paymentPlanInstallmentTitle);
  const dateRequestedLocalDate = dateRequested;
  const daysBetweenDates = printDaysBetweenDates(today, dateRequestedLocalDate);
  const otpKey = transaction.paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN ? requestKey : groupKey;
  const markAsPaidEnabled = getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false);
  const isCancellingRef = useRef(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const cancelOneTimePaymentRequest = async () => {
    if (isCancellingRef.current) return;
    isCancellingRef.current = true;
    setIsCancelling(true);
    try {
      await cancelOneTimePayment({ otpKey, tenantFirstName, paymentType, paymentPlanInstallmentTitle });
    } finally {
      isCancellingRef.current = false;
      setIsCancelling(false);
    }
  };

  const remindTenantToPayOneTimePayment = (): void => {
    const tenantEmail = tenant.email;
    return props.remindTenantToPayOneTimePayment({ tenantEmail, requestKey });
  };

  const showMarkAsPaid = markAsPaidEnabled && paymentType !== ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN;
  const showUndoOfflineMark = showMarkAsPaid && showPaidOffline && Boolean(dateReceived);
  const showOfflineMarkNote = showUndoOfflineMark && Boolean(manualPaymentNotes);
  const isUnpaid = landlordStatus === TransactionConstants.STATUS.UNPAID;
  const isPaymentFailed = landlordStatus === TransactionConstants.STATUS.PAYMENT_FAILED;
  const isUncollected = isUnpaid || isPaymentFailed;

  const renderUncollectedActions = (): React.ReactNode => {
    if (!showMarkAsPaid) {
      return isUnpaid ? (
        <Text>
          <Link onClick={isCancelling ? undefined : cancelOneTimePaymentRequest}>Cancel request</Link>
          <span className="inset-xs">|</span>
          <Link onClick={remindTenantToPayOneTimePayment}>Remind the tenant</Link>
        </Text>
      ) : null;
    }

    return (
      <ButtonGroup aria-label="Payment actions">
        <MarkAsPaidAction
          heading={paymentTypeLabel}
          subheading={`Requested ${formatDate(dateRequested, 'MMM d, yyyy')} · ${tenant.name}`}
          amount={centsToDollars(amount)}
          propertyId={propertyId}
          target={{ kind: 'oneTimePayment', groupKey }}
        />
        {isUnpaid && (
          <Menu
            trigger={
              <IconButton size="sm" tone="brand" emphasis="outlined" title="More payment actions">
                <Icon>
                  <IconMoreFilled />
                </Icon>
              </IconButton>
            }
          >
            <Menu.Item onClick={cancelOneTimePaymentRequest} disabled={isCancelling}>
              <Menu.ItemLabel>Cancel request</Menu.ItemLabel>
            </Menu.Item>
            <Menu.Item onClick={remindTenantToPayOneTimePayment}>
              <Menu.ItemLabel>Remind the tenant</Menu.ItemLabel>
            </Menu.Item>
          </Menu>
        )}
      </ButtonGroup>
    );
  };

  return (
    <Row>
      <Col xs={0} sm={0} md={10} lg={10} xl={10} xxl={10}>
        <Row>
          <Col>
            <Text className="fs-exclude" emphasize>
              {paymentTypeLabel}
            </Text>
            {notes && <Text>{notes}</Text>}
            {getStatusText(landlordStatus, dateReceived)}
            <Text className="fs-exclude">{tenant.name}</Text>
            {paymentCategory === 'completed' && (
              <Text className="fs-exclude">{getBankType(paymentCategory, accountType)}</Text>
            )}
            {showPaidOffline && (
              <Text className="fs-exclude">
                {markAsPaidEnabled && dateReceived ? 'Marked as paid' : 'Paid offline'}
              </Text>
            )}
            {landlordStatus === TransactionConstants.STATUS.MANUAL && Boolean(dateReceived) && (
              <Text className="fs-exclude">Received: {formatDate(dateReceived, 'M/d/yy')}</Text>
            )}
            {showOfflineMarkNote && manualPaymentNotes && <OfflineMarkNote note={manualPaymentNotes} />}
            {showUndoOfflineMark && (
              <UndoOfflineMarkAction
                propertyId={propertyId}
                notes={notes}
                target={{ kind: 'oneTimePayment', groupKey }}
              />
            )}
            {isUncollected && renderUncollectedActions()}
            {paymentCategory === 'upcoming' && (
              <Text className="fs-exclude" lowlight>
                Requested {daysBetweenDates}
              </Text>
            )}
            {getPaymentSent(landlordStatus, dateCreated)}
          </Col>
        </Row>
      </Col>
      <Col xs={0} sm={0} md={2} lg={2} xl={2} xxl={2}>
        <Row>
          <Col className="Dashboard-Price-Badge-Padding txtR">
            <Text type="body" className="Dashboard-Price fs-exclude">
              {centsToDollars(amount)}
            </Text>
            {landlordStatus === TransactionConstants.STATUS.PENDING && (
              <Tag size="sm" className="fs-exclude" tone="gray">
                Pending
              </Tag>
            )}
          </Col>
        </Row>
      </Col>
      <Col xs={8} sm={8} md={0} lg={0} xl={0} xxl={0} className="paymentDetailsRow-Mobile">
        <Row>
          <Col>
            <Text className="fs-exclude" emphasize>
              {paymentTypeLabel}
            </Text>
            {notes && <Text>{notes}</Text>}
            {getStatusText(landlordStatus, dateReceived)}
            <Text className="fs-exclude">{tenant.name}</Text>
            {paymentCategory === 'completed' && (
              <Text className="fs-exclude">{getBankType(paymentCategory, accountType)}</Text>
            )}
            {showPaidOffline && (
              <Text className="fs-exclude">
                {markAsPaidEnabled && dateReceived ? 'Marked as paid' : 'Paid offline'}
              </Text>
            )}
            {landlordStatus === TransactionConstants.STATUS.MANUAL && Boolean(dateReceived) && (
              <Text className="fs-exclude">Received: {formatDate(dateReceived, 'M/d/yy')}</Text>
            )}
            {showOfflineMarkNote && manualPaymentNotes && <OfflineMarkNote note={manualPaymentNotes} />}
            {showUndoOfflineMark && (
              <UndoOfflineMarkAction
                propertyId={propertyId}
                notes={notes}
                target={{ kind: 'oneTimePayment', groupKey }}
              />
            )}
            {isUncollected && showMarkAsPaid && renderUncollectedActions()}
            {isUnpaid && !showMarkAsPaid && (
              <Link onClick={isCancelling ? undefined : cancelOneTimePaymentRequest}>
                <Text>Cancel payment</Text>
              </Link>
            )}
            {paymentCategory === 'upcoming' && <Text lowlight>Requested {daysBetweenDates}</Text>}
            {getPaymentSent(landlordStatus, dateCreated)}
          </Col>
        </Row>
      </Col>
      <Col xs={4} sm={4} md={0} lg={0} xl={0} xxl={0} className="paymentDetailsRow-Mobile">
        <Col xs={12} sm={12} md={0} lg={0} xl={0} xxl={0}>
          <Row>
            <Col className="Dashboard-Price-Badge-Padding txtR">
              <Text type="body" className="Dashboard-Price fs-exclude">
                {centsToDollars(amount)}
              </Text>
              {landlordStatus === TransactionConstants.STATUS.PENDING && (
                <Tag size="sm" className="fs-exclude" tone="gray">
                  Pending
                </Tag>
              )}
            </Col>
          </Row>
        </Col>
      </Col>
    </Row>
  );
};
export default OneTimePaymentsRow;
